import React, { FC, useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { clearState, register } from '../../store/authSlice';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import jwt_decode from 'jwt-decode';

// material-ui
import {
	Checkbox,
	Divider,
	FormControlLabel,
	FormHelperText,
	Grid,
	Link,
	IconButton,
	InputAdornment,
	Stack,
	Typography,
	TextField,
	Autocomplete,
	Box
} from '@mui/material';
import LoadingButton from "@mui/lab/LoadingButton";
import countries from "../../assets/countries.json";

// third party
import * as Yup from 'yup';
import { Formik } from 'formik';
import SocialIcons from '../../components/SocialIcons/SocialIcons';


// assets
import { EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';

interface LoginProps {
	setType: (k: any) => void;
}

const SignUpForm: FC<LoginProps> = ({setType}) => {
	const { isLoggedIn, isError, errorMessage } = useAppSelector((state) => state.auth);
	const dispatch = useAppDispatch();
	const navigate = useNavigate()
	const [preFillEmail, setPreFillEmail] = useState("");

	const [searchParams, setSearchParams] = useSearchParams();

	const [showPassword, setShowPassword] = React.useState(false);
	const [isSubmit, setIsSubmit] = React.useState(false);
	const [countryCode, setCountryCode] = useState({
		code: "IN",
		dial_code: "+91",
		name: "India"
	});

	const handleClickShowPassword = () => {
		setShowPassword(!showPassword);
	};

	const handleMouseDownPassword = (event: React.MouseEvent<HTMLElement>) => {
		event.preventDefault();
	};

	// Clear state on sign in page load.
	useEffect(() => {
		return () => {
			dispatch(clearState());
		};
	}, [dispatch]);

	// Update UI based on the state(Success or Error)
	useEffect(() => {

		let token: any = searchParams.get('inviteToken');
		if (token) {
			let jwtToken: any = jwt_decode(token);
			setPreFillEmail(jwtToken['email']);
		}

		if (isError) {
			dispatch(clearState());
		}
		if (isLoggedIn) {
			dispatch(clearState());
			navigate('/home', { replace: true });
		}
	}, [isError, errorMessage, isLoggedIn, dispatch, navigate, searchParams]);

	return (
			<>
			<Formik
				initialValues={{
					name: '',
					lastname: '',
					email: preFillEmail,
					phone: '',
					password: '',
					agreeTerms: false,
					submit: null
				}}
				enableReinitialize
				validationSchema={Yup.object().shape({
					name: Yup.string().max(255).required('First Name is required'),
					lastname: Yup.string().max(255).required('Last Name is required'),
					email: Yup.string().email('Must be a valid email').max(255).required('Email is required'),
					password: Yup.string().max(255).required('Password is required')
						.min(8, 'Password is too short - should be 8 chars minimum.')
						.matches(/^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/, 'Password should contain alpha numeric and special character.'),
					agreeTerms: Yup.boolean().oneOf([true], 'You must agree to the Terms of Service and Privacy Policy')
				})}
				onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
					try {
						setStatus({ success: false });
						setSubmitting(false);
						setIsSubmit(true);
						const { name, email, phone, password } = values;

						let phone1 = countryCode.dial_code + phone;
						const res = await dispatch(register({ name: name, email: email, phone: phone1, password: password }));
						setIsSubmit(false);
						if (register.fulfilled.match(res)) window.location.href = '/account/create-basic-info';
						else setErrors({ submit: (res.payload as any)?.message || 'Sign up failed' } as any);
					} catch (err) {
						//console.error(err);
						setStatus({ success: false });
						//setErrors({ submit: err.message });
						setSubmitting(false);
						setIsSubmit(false);
					}
					//
				}}
			>
				{({ errors, handleBlur, handleChange, handleSubmit, touched, values }) => (
					<form noValidate onSubmit={handleSubmit} style={{paddingLeft: '35px'}}>
						<Typography style={{float:'right',marginTop:10}} component={Link} onClick={()=> setType('0')} variant="body1" sx={{ textDecoration: 'none' }} color="#aaa">
						Already have an account?
                                  </Typography>
						<h3 className="mb-4">Sign In</h3>

						{/* <div className="form-group">
						<div class="form-wrapper">
							<label for="">First Name</label>
							<input type="text" class="form-control">
						</div>
						<div class="form-wrapper">
							<label for="">Last Name</label>
							<input type="text" class="form-control">
						</div>
					</div> */}
						<div className="form-group">
							<div className="form-wrapper">
								<TextField
									id="name-login"
									type="name"
									className='c-text'
									variant="standard"
									sx={{ width: '100%'}}
									InputProps={{
										disableUnderline: true,
									}}
									size='small'
									value={values.name}
									name="name"
									onBlur={handleBlur}
									onChange={handleChange}
									placeholder="First Name"
									fullWidth
									error={Boolean(touched.name && errors.name)}
								/>
								{touched.name && errors.name && (
									<FormHelperText error id="helper-text-name-signup">
										{errors.name}
									</FormHelperText>
								)}
							</div>
							<div className="form-wrapper">
								<TextField
									fullWidth
									error={Boolean(touched.lastname && errors.lastname)}
									id="lastname-signup"
									type="lastname"
									className='c-text'
									variant="standard"
									sx={{ width: '100%'}}
									InputProps={{
										disableUnderline: true,
									}}
									size='small'
									value={values.lastname}
									name="lastname"
									onBlur={handleBlur}
									onChange={handleChange}
									placeholder="Last Name"
									inputProps={{}}
								/>
								{touched.lastname && errors.lastname && (
									<FormHelperText error id="helper-text-lastname-signup">
										{errors.lastname}
									</FormHelperText>
								)}
							</div>
							</div>
							<div className="form-wrapper">
								<Autocomplete
									id="country-select-demo"
									options={countries}
									autoHighlight
									value={countryCode}
									getOptionLabel={(option) => option.dial_code}
									renderOption={(props, option) => (
										<Box
											component="li"
											sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
											{...props}
										>
											<img
												loading="lazy"
												width="20"
												src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
												srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
												alt=""
											/>
											{option.dial_code}
										</Box>
									)}
									onChange={(event, newValue) => {
										if (newValue) setCountryCode(newValue);
									}}
									
									renderInput={(params) => (
										<TextField
											{...params}
											className='c-text'
											variant="standard"
											sx={{ width: '100%'}}
											size='small'
											InputProps={{
												...params.InputProps,
												disableUnderline: true,
												autoComplete: "new-password", // disable autocomplete and autofill
											}}
										/>
									)}
								/>
							</div>
							<div className="form-wrapper">
								<TextField
									fullWidth
									error={Boolean(touched.phone && errors.phone)}
									id="phone"
									className='c-text'
									variant="standard"
									sx={{ width: '100%'}}
									InputProps={{
										disableUnderline: true,
									}}
									size='small'
									value={values.phone}
									name="phone"
									onBlur={handleBlur}
									onChange={handleChange}
									placeholder="Phone"
									inputProps={{}}
								/>
								{touched.phone && errors.phone && (
									<FormHelperText error id="helper-text-phone-signup">
										{errors.phone}
									</FormHelperText>
								)}
							</div>
							<div className="form-wrapper">
								<TextField
									fullWidth
									error={Boolean(touched.email && errors.email)}
									id="email-login"
									type="email"
									className='c-text'
									variant="standard"
									sx={{ width: '100%'}}
									InputProps={{
										disableUnderline: true,
									}}
									size='small'
									value={values.email}
									name="email"
									onBlur={handleBlur}
									onChange={handleChange}
									placeholder="eg. demo@company.com"
									inputProps={{}}
								/>
								{touched.email && errors.email && (
									<FormHelperText error id="helper-text-email-signup">
										{errors.email}
									</FormHelperText>
								)}
							</div>
								<TextField
									fullWidth
									error={Boolean(touched.password && errors.password)}
									id="password-signup"
									variant='standard'
									type={showPassword ? 'text' : 'password'}
									className='c-text'
                              		size='small'
									value={values.password}
									name="password"
									onBlur={handleBlur}
									onChange={(e) => {
										handleChange(e);
									}}
									InputProps={{
										disableUnderline: true,
										endAdornment:
											<InputAdornment position="end">
												<IconButton style={{marginRight:-8,marginTop:-2}}
													aria-label="toggle password visibility"
													onClick={handleClickShowPassword}
													onMouseDown={handleMouseDownPassword}
													edge="end"
													size="large"
												>
													{showPassword ? <EyeOutlined style={{fontSize:14}}/> : <EyeInvisibleOutlined style={{fontSize:14}}/>}
												</IconButton>
											</InputAdornment>
									}}
									placeholder="******"
									inputProps={{}}
								/>
								{touched.password && errors.password && (
									<FormHelperText error id="helper-text-password-signup">
										{errors.password}
									</FormHelperText>
								)}
								<FormControlLabel
									sx={{ mt: 2, alignItems: 'flex-start', ml: 0 }}
									control={
										<Checkbox
											size="small"
											color="primary"
											name="agreeTerms"
											checked={values.agreeTerms}
											onChange={handleChange}
											sx={{ p: 0, mr: 0.75, mt: '2px' }}
										/>
									}
									label={
										<Typography variant="body2">
											By Signing up, you agree to our &nbsp;
											<Link variant="subtitle2" component={RouterLink} to="/terms-of-use" color='primary.light'>
												Terms of Service
											</Link>
											&nbsp; and &nbsp;
											<Link variant="subtitle2" component={RouterLink} to="/privacy-policy" color='primary.light'>
												Privacy Policy
											</Link>
										</Typography>
									}
								/>
								{touched.agreeTerms && errors.agreeTerms && (
									<FormHelperText error>{errors.agreeTerms}</FormHelperText>
								)}
								{errors.submit && (
									<FormHelperText error>{errors.submit}</FormHelperText>
								)}
								<div style={{width:'100%'}}>
								<LoadingButton
									className="c-button"
									disableElevation
									type="submit"
									loading={isSubmit}
									style={{width:'60%',margin:'auto'}}
								>
									{'Create Account'}
								</LoadingButton>
								</div> 
								<Grid item xs={12} mt={3} mb={3}>
								<Divider>
									<Typography variant="caption">Sign up with</Typography>
								</Divider>
								</Grid>
								<Grid item xs={12}>
									<SocialIcons />
								</Grid>
							</form>
					)}
						</Formik>
					</>
				);
};

				export default SignUpForm;
